import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Crown, Check, Lock, Sparkles, Coins, ShoppingBag, Package, Percent, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const RARITY = {
  common: { name: "Common", color: "border-zinc-700 text-zinc-300", badge: "bg-zinc-800 text-zinc-300", glow: "none" },
  rare: { name: "Rare", color: "border-sky-500 text-sky-300", badge: "bg-sky-950 text-sky-400 border border-sky-500/30", glow: "0 0 20px rgba(56,189,248,0.2)" },
  epic: { name: "Epic", color: "border-purple-500 text-purple-300", badge: "bg-purple-950 text-purple-400 border border-purple-500/30", glow: "0 0 20px rgba(168,85,247,0.2)" },
  legendary: { name: "Legendary", color: "border-[var(--tf-primary)] text-orange-300", badge: "bg-orange-950 text-orange-400 border border-orange-500/40", glow: "0 0 25px rgba(255,79,0,0.3)" },
  mythic: { name: "Mythic", color: "border-pink-500 text-pink-300", badge: "bg-pink-950 text-pink-400 border border-pink-500/40", glow: "0 0 30px rgba(236,72,153,0.4)" },
};

export default function Marketplace() {
  const { user, refresh } = useAuth();
  const [frames, setFrames] = useState([]);
  const [filter, setFilter] = useState("all");
  const [buying, setBuying] = useState(null);

  const isVip = Boolean(user && (user.is_vip || user.role === "admin" || user.role === "root"));

  const load = () => api.get("/idg/marketplace/frames").then((r) => setFrames(r.data)).catch(() => {});

  useEffect(() => { if (user) load(); }, [user]);

  const buy = async (frame) => {
    if (!user) { toast.error("Login necesar"); return; }
    const finalPrice = frame.price_points;
    if ((user.points || 0) < finalPrice) { 
      toast.error(`Puncte IDG insuficiente. Ai nevoie de ${finalPrice} IDG, ai ${user.points || 0} IDG.`); 
      return; 
    }
    if (!window.confirm(`Cumperi rama "${frame.name}" pentru ${finalPrice} IDG Points?${isVip ? " (Reducere 50% VIP inclusă!)" : ""}`)) return;
    
    setBuying(frame.id);
    try {
      const { data } = await api.post("/idg/marketplace/purchase", { frame_id: frame.id });
      toast.success(`🎉 Cumpărat cu succes! Puncte rămase: ${data.remaining_points} IDG`);
      if (refresh) await refresh();
      load();
    } catch (e) { 
      toast.error(formatApiError(e)); 
    } finally { 
      setBuying(null); 
    }
  };

  if (!user) return (
    <div className="text-center py-20 bg-[#0b0e15] border border-white/10 p-8 max-w-xl mx-auto space-y-4" data-testid="marketplace-login">
      <Package className="w-12 h-12 text-[var(--tf-primary)] mx-auto" />
      <h1 className="font-chakra text-2xl font-black text-white uppercase tracking-wider">VIP MARKETPLACE</h1>
      <p className="text-zinc-400 text-xs font-mono">// Autentifică-te pentru a vizualiza magazinul de cosmetice și rame VIP</p>
      <Link to="/login" className="tf-btn-primary tf-clip-btn inline-block px-6 py-2.5 text-xs font-black uppercase tracking-wider">
        AUTENTIFICARE
      </Link>
    </div>
  );

  const filtered = filter === "all" ? frames : filter === "vip" ? frames.filter(f => f.required_role === "vip") : frames.filter((f) => f.rarity === filter);

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="marketplace-page">
      {/* 1. Hero Command Header */}
      <div className="bg-[#0b0e15] border border-white/10 p-4 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-[var(--tf-primary)]/10 via-transparent to-transparent pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <div 
            className="w-12 h-12 flex items-center justify-center text-black font-black shrink-0 shadow-lg"
            style={{ backgroundColor: "var(--tf-primary)" }}
          >
            <ShoppingBag className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-chakra text-2xl sm:text-3xl font-black uppercase tracking-wider text-white">
                VIP & IDG MARKETPLACE
              </h1>
              {isVip && (
                <span className="bg-amber-500/20 text-amber-400 border border-amber-500/40 text-[10px] font-black font-chakra px-2 py-0.5 uppercase tracking-widest flex items-center gap-1 shadow-md">
                  <Percent className="w-3 h-3" /> 50% REDUCERE ACTIVĂ
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400 font-mono mt-0.5">
              // Deblochează peste 90 de rame exclusive de avatar și cosmetice folosind IDG Points
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 relative z-10 bg-[#07090e] border border-white/10 px-4 py-2">
          <Coins className="w-5 h-5 text-amber-400" />
          <div>
            <div className="text-[10px] font-chakra font-bold text-zinc-500 uppercase tracking-wider">Balanță IDG Points</div>
            <div className="font-chakra font-black text-amber-400 text-lg">{user.points || 0} IDG</div>
          </div>
        </div>
      </div>

      {/* VIP Promotion Banner */}
      {!isVip ? (
        <div className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-transparent border border-amber-500/30 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
              <Crown className="w-5 h-5" />
            </div>
            <div>
              <span className="font-chakra font-black text-sm text-amber-400 uppercase tracking-wider block">
                DEVINO MEMBRU VIP ȘI ECONOMISEȘTI 50% LA TOATE RAMELE!
              </span>
              <p className="text-xs text-zinc-300 font-mono mt-0.5">
                Membrii VIP beneficiază de preț redus la jumătate (-50%) la fiecare achiziție și acces la colecția Mythic & Legendary.
              </p>
            </div>
          </div>
          <Link to="/vip" className="bg-amber-500 hover:bg-amber-400 text-black px-5 py-2 text-xs font-chakra font-black uppercase tracking-wider whitespace-nowrap shadow-lg transition-all">
            OBȚINE VIP ACUM
          </Link>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-emerald-500/15 via-transparent to-transparent border border-emerald-500/30 p-3.5 flex items-center gap-3">
          <Zap className="w-5 h-5 text-emerald-400 shrink-0" />
          <p className="text-xs font-mono text-emerald-300">
            <span className="font-bold font-chakra uppercase tracking-wide text-white">Statut VIP Activ:</span> Ai aplicată o reducere permanentă de <span className="font-bold text-emerald-400">50% reducere</span> pe toate ramele din magazin!
          </p>
        </div>
      )}

      {/* 2. Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="marketplace-filters">
        {[
          { key: "all", label: "Toate Ramele (90+)" },
          { key: "vip", label: "★ Doar VIP" },
          { key: "mythic", label: "Mythic" },
          { key: "legendary", label: "Legendary" },
          { key: "epic", label: "Epic" },
          { key: "rare", label: "Rare" },
          { key: "common", label: "Common" },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`px-3.5 py-1.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
              filter === item.key 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#0b0e15] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* 3. Items Grid */}
      {filtered.length === 0 ? (
        <div className="bg-[#0b0e15] border border-white/10 p-12 text-center text-zinc-500 font-mono text-xs">
          Nu există iteme disponibile în această categorie momentan.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((f) => {
            const rar = RARITY[f.rarity] || RARITY.common;
            const canAfford = (user.points || 0) >= f.price_points;
            const needsVip = f.required_role === "vip" && !isVip;
            const frameSrc = encodeURI(`${process.env.PUBLIC_URL || ''}${f.image_url}`);

            return (
              <div
                key={f.id}
                className={`bg-[#0b0e15] p-4 border ${rar.color} flex flex-col justify-between relative transition-all hover:scale-[1.02] shadow-xl group`}
                style={{ boxShadow: rar.glow }}
                data-testid={`marketplace-frame-${f.slug}`}
              >
                <div>
                  {/* Visual Preview Container */}
                  <div className="aspect-square border border-white/10 mb-3 relative bg-[#07090e] flex items-center justify-center overflow-hidden">
                    {/* Dummy Player Avatar in Background */}
                    <div className="w-16 h-16 rounded-full overflow-hidden border border-white/20 bg-black/60 shadow-inner">
                      <img 
                        src={`https://ui-avatars.com/api/?background=121622&color=fff&name=${user.username || 'Player'}`} 
                        alt="" 
                        className="w-full h-full object-cover opacity-80"
                      />
                    </div>

                    {/* Frame Overlay */}
                    <img 
                      src={frameSrc} 
                      alt={f.name} 
                      className="absolute inset-0 w-full h-full object-contain pointer-events-none drop-shadow-[0_0_12px_rgba(0,0,0,0.8)]"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />

                    {f.owned && (
                      <div className="absolute top-2 right-2 bg-emerald-600 text-white px-2 py-0.5 text-[9px] font-black uppercase tracking-wider font-chakra flex items-center gap-1 shadow-md">
                        <Check className="w-3 h-3" /> DEȚINUT
                      </div>
                    )}

                    {f.required_role === "vip" && (
                      <div className="absolute top-2 left-2 bg-amber-500 text-black px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest font-chakra flex items-center gap-0.5 shadow-md">
                        <Crown className="w-2.5 h-2.5" /> VIP
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-1.5 mb-1">
                    <h3 className="font-chakra font-black text-xs text-white uppercase truncate" title={f.name}>{f.name}</h3>
                    <span className={`px-1.5 py-0.2 text-[8px] font-black uppercase font-chakra shrink-0 ${rar.badge}`}>
                      {rar.name}
                    </span>
                  </div>

                  <p className="text-[11px] text-zinc-400 font-mono line-clamp-2 mb-3 leading-tight">
                    {f.description || "Ramă estetică pentru profilul tău."}
                  </p>
                </div>

                <div className="pt-2.5 border-t border-white/5 flex items-center justify-between gap-2">
                  <div>
                    {isVip && f.original_price > f.price_points ? (
                      <div className="flex flex-col">
                        <span className="text-[10px] text-zinc-500 line-through font-mono">
                          {f.original_price} IDG
                        </span>
                        <span className="font-chakra font-black text-sm text-emerald-400">
                          {f.price_points} IDG <span className="text-[9px] text-amber-400 font-bold">-50%</span>
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        <span className="font-chakra font-black text-sm text-amber-400">
                          {f.price_points} IDG
                        </span>
                        {!isVip && (
                          <span className="text-[9px] text-zinc-500 font-mono">
                            ({Math.round(f.price_points / 2)} IDG cu VIP)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {f.owned ? (
                    <Link
                      to="/loadout"
                      className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-chakra font-bold text-zinc-300 hover:text-white uppercase transition-colors"
                    >
                      Echipează
                    </Link>
                  ) : (
                    <button
                      onClick={() => buy(f)}
                      disabled={buying === f.id || !canAfford || needsVip}
                      className={`px-3 py-1.5 text-[10px] font-chakra font-black uppercase tracking-wider transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                        needsVip
                          ? "bg-zinc-800 text-zinc-400 border border-zinc-700"
                          : "tf-btn-primary tf-clip-btn text-white"
                      }`}
                    >
                      {buying === f.id ? "..." : needsVip ? "Doar VIP" : "Cumpără"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
